import {LitElement} from 'lit';
import {state} from 'lit/decorators.js';
import {clamLoginStyles} from './clam-login.css.js';
import {renderLogin} from './clam-login.html.js';
import {authService} from '../../auth/auth.service.js';
import {pendingChallengeSignal} from '../../auth/auth.store.js';
import type {AuthPrompt, PasswordRule} from '../../auth/auth.types.js';

type LoginField =
    | 'loginEmail'
    | 'loginPassword'
    | 'mfaCode'
    | 'newPassword'
    | 'newPasswordConfirm'
    | 'firstName'
    | 'lastName'
    | 'forgotEmail'
    | 'resetCode'
    | 'resetPassword';

export class ClamLogin extends LitElement {
    static styles = clamLoginStyles;

    @state() private _prompt: AuthPrompt = 'login';
    @state() private _loading = false;
    @state() private _error = '';
    @state() private _success = '';
    @state() private _showPassword = false;
    @state() private _passwordRules: PasswordRule[] = [];
    @state() private _missingAttributes: string[] = [];

    @state() private loginEmail = '';
    @state() private loginPassword = '';
    @state() private mfaCode = '';
    @state() private newPassword = '';
    @state() private newPasswordConfirm = '';
    @state() private firstName = '';
    @state() private lastName = '';
    @state() private forgotEmail = '';
    @state() private resetCode = '';
    @state() private resetPassword = '';

    connectedCallback(): void {
        super.connectedCallback();
        if (authService.isLoggedIn) {
            this._goHome();
        }
    }

    private _goHome(): void {
        this.dispatchEvent(
            new CustomEvent('authed', {bubbles: true, composed: true}),
        );
    }

    private _clearMessages(): void {
        this._error = '';
        this._success = '';
    }

    private _setField(field: LoginField, value: string): void {
        (this as unknown as Record<LoginField, string>)[field] = value;
        this.requestUpdate();
    }

    private _onNewPasswordInput(value: string): void {
        this._passwordRules = authService.validatePasswordPolicy(value).rules;
    }

    private _togglePassword(): void {
        this._showPassword = !this._showPassword;
    }

    private _goToReset(): void {
        this._clearMessages();
        this._prompt = 'forgotPassword';
    }

    private _goToLogin(): void {
        this._clearMessages();
        this._prompt = 'login';
    }

    private async _submit(): Promise<void> {
        switch (this._prompt) {
            case 'login':
                return this._enter();
            case 'mfaCode':
                return this._verifyMFA();
            case 'newPassword':
                return this._setNewPassword();
            case 'forgotPassword':
                return this._requestReset();
            case 'confirmReset':
                return this._confirmReset();
        }
    }

    private async _enter(): Promise<void> {
        this._clearMessages();
        if (!this.loginEmail || !this.loginPassword) {
            this._error = 'Ingresa email y contrasena';
            return;
        }
        this._loading = true;
        try {
            const result = await authService.login(this.loginEmail, this.loginPassword);
            this._loading = false;
            if (result.success) {
                this._goHome();
            } else if (result.prompt) {
                this._prompt = result.prompt;
                // Capturar atributos requeridos para NEW_PASSWORD_REQUIRED.
                this._missingAttributes = pendingChallengeSignal.value?.missingAttributes ?? [];
                if (result.message) this._error = result.message;
            } else {
                this._error = result.message ?? 'Credenciales incorrectas';
            }
        } catch {
            this._loading = false;
            this._error = 'Error al iniciar sesion. Intenta de nuevo.';
        }
    }

    private async _verifyMFA(): Promise<void> {
        this._clearMessages();
        if (!this.mfaCode) {
            this._error = 'Ingresa el codigo de verificacion';
            return;
        }
        this._loading = true;
        try {
            const result = await authService.verifyMFACode(this.mfaCode);
            this._loading = false;
            if (result.success) {
                this._goHome();
            } else {
                this._error = result.message ?? 'Codigo incorrecto';
            }
        } catch {
            this._loading = false;
            this._error = 'Error al verificar codigo.';
        }
    }

    private async _setNewPassword(): Promise<void> {
        this._clearMessages();
        if (!this.newPassword || !this.newPasswordConfirm) {
            this._error = 'Completa todos los campos';
            return;
        }
        if (this.newPassword !== this.newPasswordConfirm) {
            this._error = 'Las contrasenas no coinciden';
            return;
        }
        // Validar atributos requeridos por Cognito (name, family_name, etc.).
        const needsName = this._missingAttributes.includes('name');
        const needsFamily = this._missingAttributes.includes('family_name');
        if ((needsName && !this.firstName.trim()) || (needsFamily && !this.lastName.trim())) {
            this._error = 'Completa nombre y apellido';
            return;
        }
        const policy = authService.validatePasswordPolicy(this.newPassword);
        if (!policy.valid) {
            this._error = 'La contrasena no cumple los requisitos';
            return;
        }
        // Construir userAttributes solo con los que el challenge exige.
        const userAttributes: Record<string, string> = {};
        if (needsName) userAttributes.name = this.firstName.trim();
        if (needsFamily) userAttributes.family_name = this.lastName.trim();

        this._loading = true;
        try {
            const result = await authService.confirmNewPassword(
                this.newPassword,
                Object.keys(userAttributes).length > 0 ? userAttributes : undefined,
            );
            this._loading = false;
            if (result.success) {
                this._goHome();
            } else {
                this._error = result.message ?? 'No se pudo cambiar la contrasena';
            }
        } catch {
            this._loading = false;
            this._error = 'Error al cambiar contrasena.';
        }
    }

    private async _requestReset(): Promise<void> {
        this._clearMessages();
        if (!this.forgotEmail) {
            this._error = 'Ingresa tu email';
            return;
        }
        this._loading = true;
        try {
            const result = await authService.forgotPassword(this.forgotEmail);
            this._loading = false;
            if (result.success) {
                this._success = 'Codigo enviado a tu email';
                this._prompt = 'confirmReset';
            } else {
                this._error = result.message ?? 'Error al enviar codigo';
            }
        } catch {
            this._loading = false;
            this._error = 'Error al enviar codigo. Intenta de nuevo.';
        }
    }

    private async _confirmReset(): Promise<void> {
        this._clearMessages();
        if (!this.forgotEmail || !this.resetCode || !this.resetPassword) {
            this._error = 'Completa todos los campos';
            return;
        }
        this._loading = true;
        try {
            const result = await authService.confirmForgotPassword(
                this.forgotEmail,
                this.resetCode,
                this.resetPassword,
            );
            this._loading = false;
            if (result.success) {
                this._success = 'Contrasena actualizada. Ya puedes iniciar sesion.';
                this._prompt = 'login';
                this.loginPassword = '';
                this.resetCode = '';
                this.resetPassword = '';
            } else {
                this._error = result.message ?? 'Error al cambiar contrasena';
            }
        } catch {
            this._loading = false;
            this._error = 'Error al cambiar contrasena. Verifica el codigo.';
        }
    }

    render() {
        return renderLogin({
            prompt: this._prompt,
            loading: this._loading,
            error: this._error,
            success: this._success,
            showPassword: this._showPassword,
            passwordRules: this._passwordRules,
            missingAttributes: this._missingAttributes,
            loginEmail: this.loginEmail,
            loginPassword: this.loginPassword,
            mfaCode: this.mfaCode,
            newPassword: this.newPassword,
            newPasswordConfirm: this.newPasswordConfirm,
            firstName: this.firstName,
            lastName: this.lastName,
            forgotEmail: this.forgotEmail,
            resetCode: this.resetCode,
            resetPassword: this.resetPassword,
            onInputChange: (field, value) => this._setField(field as LoginField, value),
            onNewPasswordInput: (v) => this._onNewPasswordInput(v),
            togglePassword: () => this._togglePassword(),
            submit: () => {
                void this._submit();
            },
            goToReset: () => this._goToReset(),
            goToLogin: () => this._goToLogin(),
        });
    }
}

customElements.define('clam-login', ClamLogin);

declare global {
    interface HTMLElementTagNameMap {
        'clam-login': ClamLogin;
    }
}
